import React, { useMemo, useRef, useState } from 'react';
import { FiUploadCloud, FiFileText, FiTrash2, FiDownload, FiArrowRight, FiLoader, FiAlertTriangle } from 'react-icons/fi';
import apiClient from '../../../lib/api/apiClient';
import { addRow, getTableData } from '../../db-viewer/data/dataAccess';
import { localExtractStatement } from '../utils/localStatementExtract';
import { storeDocBlob, isServerKey } from '../utils/docStorage';
import './ConvertToCsvTab.css';

type Engine = 'local' | 'claude';

// =============================================
// Upload to Convert to .csv — bank/credit-card statement → Claude vision →
// editable transaction grid → import into acctg_bank_txn → CSV download.
// The extraction runs on the server (/api/extract/bank-statement); without an
// ANTHROPIC_API_KEY the server returns a labelled dev-mock so the flow is testable.
// =============================================

interface OwnerOption { id: number; name: string; type: 'personal' | 'family' | 'business'; }

interface ExtractedTxn {
  date: string;
  description: string;
  amount: number;
  direction: 'debit' | 'credit';
  balance: number | null;
}

interface RowState extends ExtractedTxn { keep: boolean; raw?: string; }

const ACCEPT = '.pdf,.png,.jpg,.jpeg';

function mediaTypeFor(file: File): string | null {
  const t = (file.type || '').toLowerCase();
  if (t === 'application/pdf') return 'application/pdf';
  if (t === 'image/png') return 'image/png';
  if (t === 'image/jpeg' || t === 'image/jpg') return 'image/jpeg';
  const ext = file.name.toLowerCase().split('.').pop();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  return null;
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)) as any);
  }
  return btoa(binary);
}

function resolveAccountOwnerId(): number | null {
  const users = getTableData('users');
  // eslint-disable-next-line eqeqeq
  const owner = users.find(u => u.owner_id == null || u.owner_id === '');
  return owner ? Number(owner.user_id) : (users[0] ? Number(users[0].user_id) : null);
}

// gzip + base64 (matches UploadDocsPage so ProcessReceiptsTab can decompress it)
async function gzipBase64(data: Uint8Array): Promise<{ base64: string; size: number }> {
  const CS = (window as any).CompressionStream;
  if (typeof CS !== 'undefined') {
    const cs = new CS('gzip');
    const writer = cs.writable.getWriter();
    writer.write(data);
    writer.close();
    const chunks: Uint8Array[] = [];
    const reader = cs.readable.getReader();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const total = chunks.reduce((s, c) => s + c.length, 0);
    const out = new Uint8Array(total);
    let off = 0;
    for (const c of chunks) { out.set(c, off); off += c.length; }
    let bin = '';
    for (let i = 0; i < out.length; i++) bin += String.fromCharCode(out[i]);
    return { base64: btoa(bin), size: out.length };
  }
  let bin = '';
  for (let i = 0; i < data.length; i++) bin += String.fromCharCode(data[i]);
  return { base64: btoa(bin), size: data.length };
}

// Save the source statement file as a dms_documents row so the imported
// transactions can link back to it (document_id) and surface inside
// Process Docs/Receipts. Returns the new document_id.
async function saveStatementDoc(
  file: File,
  accountOwnerId: number,
  ownerTypeDb: string | null,
  ownerId: string | number,
): Promise<number> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const { base64, size } = await gzipBase64(buf);
  const mt = mediaTypeFor(file) || 'application/pdf';
  const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
  const oid = ownerId === '' || ownerId == null ? null : Number(ownerId);
  // account_owner / family_member docs are keyed by user_id in Process Receipts;
  // business docs match on owner_type + owner_id, so user_id can be the manager.
  const userId = (ownerTypeDb === 'account_owner' || ownerTypeDb === 'family_member') && oid != null
    ? oid : accountOwnerId;
  const blobKey = await storeDocBlob(base64, accountOwnerId);
  const inlineBytes = isServerKey(blobKey) ? null : base64;
  const created = await addRow('dms_documents', {
    account_owner_id: accountOwnerId,
    user_id: userId,
    dms_folders_id: null,
    filename: file.name,
    original_filename: file.name,
    file_base64: inlineBytes,
    file_path: blobKey,
    file_size_original: buf.length,
    file_size_stored: size,
    mime_type: mt,
    page_count: null,
    upload_source: 'convert-csv',
    upload_date: now,
    status: 'Review',
    owner_type: ownerTypeDb,
    owner_id: oid,
    notes: 'Bank statement — source of imported transactions',
    is_active: 1,
    is_deleted: 0,
    created_by: accountOwnerId,
    created_at: now,
    updated_at: now,
  }, 'document_id');
  return Number(created.document_id);
}

const ConvertToCsvTab: React.FC<{ clients: OwnerOption[] }> = ({ clients }) => {
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [error, setError] = useState('');
  const [devMock, setDevMock] = useState(false);
  // Free browser engine is the default (no API cost). Code default via env, runtime
  // override persisted in localStorage so the Claude path is one toggle away.
  const [engine, setEngine] = useState<Engine>(() => {
    const env = (process.env.REACT_APP_EXTRACT_ENGINE as Engine) || '';
    const saved = (typeof localStorage !== 'undefined'
      ? (localStorage.getItem('akt_extract_engine') as Engine | null) : null);
    return saved || env || 'local';
  });
  const [accountLabel, setAccountLabel] = useState('');
  const [currency, setCurrency] = useState('CAD');
  const [rows, setRows] = useState<RowState[]>([]);
  const [ownerKey, setOwnerKey] = useState<string>('');   // "type:id"
  const [importedCount, setImportedCount] = useState(0);
  const [extractSource, setExtractSource] = useState('pdf_extract');
  // The uploaded statement file is kept so import can save it as a dms_document.
  const srcFileRef = useRef<File | null>(null);
  // The document_id created on the first import for this file (reused on re-import).
  const createdDocIdRef = useRef<number | null>(null);

  const ownerOptions = useMemo(() => {
    const group = (t: OwnerOption['type'], label: string) =>
      clients.filter(c => c.type === t).map(c => ({ key: `${t}:${c.id}`, label, name: c.name }));
    return [
      ...group('personal', 'Account Owner'),
      ...group('family', 'Family Member'),
      ...group('business', 'Business'),
    ];
  }, [clients]);

  const keptRows = rows.filter(r => r.keep);

  const changeEngine = (e: Engine) => {
    setEngine(e);
    try { localStorage.setItem('akt_extract_engine', e); } catch { /* ignore */ }
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const mediaType = mediaTypeFor(file);
    if (!mediaType) { setError('Please upload a PDF, PNG, or JPEG statement.'); return; }
    setError(''); setRows([]); setImportedCount(0); setDevMock(false); setProgressMsg('');
    setFileName(file.name); setBusy(true);
    srcFileRef.current = file;
    createdDocIdRef.current = null;
    try {
      let data: { account_label: string; currency: string; transactions: ExtractedTxn[]; devMock?: boolean; source?: string };
      if (engine === 'local') {
        data = await localExtractStatement(file, mediaType,
          (label, p) => setProgressMsg(`${label} ${Math.round(p * 100)}%`));
      } else {
        const fileBase64 = await fileToBase64(file);
        const resp = await apiClient.post<typeof data>('/extract/bank-statement', { fileBase64, mediaType });
        data = resp.data;
      }
      setAccountLabel(data.account_label || '');
      setCurrency(data.currency || 'CAD');
      setDevMock(!!data.devMock);
      setExtractSource(data.source || (engine === 'local'
        ? (mediaType === 'application/pdf' ? 'browser_pdf' : 'browser_ocr')
        : (mediaType === 'application/pdf' ? 'pdf_extract' : 'image_extract')));
      setRows((data.transactions || []).map(t => ({ ...t, keep: true })));
      if (!data.transactions || data.transactions.length === 0) {
        setError(engine === 'local'
          ? 'No transactions parsed — try the Claude API engine, or add rows manually after import.'
          : 'No transactions were found in this document.');
      }
    } catch (e: any) {
      setError(e?.message || 'Extraction failed.');
    } finally {
      setBusy(false); setProgressMsg('');
    }
  };

  const updateRow = (i: number, patch: Partial<RowState>) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const handleImport = async () => {
    if (keptRows.length === 0) return;
    const accountOwnerId = resolveAccountOwnerId();
    const [ownerType, ownerId] = ownerKey ? ownerKey.split(':') : ['', ''];
    const ownerTypeDb = ownerType === 'personal' ? 'account_owner'
      : ownerType === 'family' ? 'family_member'
      : ownerType === 'business' ? 'business' : null;

    setBusy(true); setError('');
    try {
      // Save the source statement once so the transactions link back to it and
      // surface inside Process Docs/Receipts. Best-effort — txns still import if it fails.
      let documentId = createdDocIdRef.current;
      if (documentId == null && srcFileRef.current && accountOwnerId != null) {
        try {
          documentId = await saveStatementDoc(srcFileRef.current, accountOwnerId, ownerTypeDb, ownerId);
          createdDocIdRef.current = documentId;
        } catch { documentId = null; }
      }

      let ok = 0;
      for (const r of keptRows) {
        await addRow('acctg_bank_txn', {
          document_id: documentId,
          account_owner_id: accountOwnerId,
          owner_type: ownerTypeDb,
          owner_id: ownerId ? Number(ownerId) : null,
          txn_date: r.date || null,
          description: r.description || '',
          amount: Number(r.amount) || 0,
          direction: r.direction,
          balance: r.balance == null ? null : Number(r.balance),
          currency,
          status: 'imported',
          source: devMock ? 'dev_mock' : extractSource,
          raw_line: r.raw || null,
          is_active: 1, is_deleted: 0,
          created_by: accountOwnerId,
        }, 'txn_id');
        ok++;
      }
      setImportedCount(ok);
    } catch (e: any) {
      setError(e?.message || 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = () => {
    const header = ['date', 'description', 'amount', 'direction', 'balance', 'currency'];
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [header.join(',')];
    for (const r of keptRows) {
      lines.push([r.date, r.description, r.amount, r.direction, r.balance ?? '', currency].map(esc).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (accountLabel || 'statement').replace(/[^a-z0-9]+/gi, '_').toLowerCase() + '_transactions.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const goToProcess = () => {
    // Auto-select the saved statement on the Process Receipts page (nav bridge).
    if (createdDocIdRef.current != null) {
      try { sessionStorage.setItem('akt_navigate_to_doc', String(createdDocIdRef.current)); } catch { /* ignore */ }
    }
    window.location.hash = 'process-receipts';
    window.dispatchEvent(new Event('hashchange'));
  };

  return (
    <div className="cc-wrap">
      {/* Engine selector — Free browser (default) vs Claude API */}
      <div className="cc-engine">
        <span className="cc-engine-label">Extraction engine</span>
        <div className="cc-seg">
          <button type="button" className={`cc-seg-btn ${engine === 'local' ? 'active' : ''}`}
            onClick={() => changeEngine('local')}>Free · Browser</button>
          <button type="button" className={`cc-seg-btn ${engine === 'claude' ? 'active' : ''}`}
            onClick={() => changeEngine('claude')}>Claude API</button>
        </div>
        <span className="cc-engine-hint">
          {engine === 'local'
            ? 'No API cost. Digital PDFs are accurate; photos/scans use slower OCR — review carefully.'
            : 'Uses the Anthropic API key (billed per page). Best accuracy on scans & photos.'}
        </span>
      </div>

      {/* First-time guidance */}
      <p className="cc-tip">
        <strong>💡 First time?</strong> A statement <em>downloaded as a PDF</em> from your online banking reads best on
        {' '}<em>Free · Browser</em> — it's instant, accurate, and costs nothing. Use <em>Claude API</em> for
        phone photos or scanned statements where accuracy matters. Either way, <strong>review the rows before importing</strong>.
      </p>

      {/* Drop / pick */}
      <label className="cc-drop">
        <input
          type="file" accept={ACCEPT} className="cc-file-input"
          onChange={e => onFile(e.target.files?.[0])}
        />
        <FiUploadCloud className="cc-drop-ic" />
        <div className="cc-drop-main">Upload a bank or credit-card statement</div>
        <div className="cc-drop-sub">PDF, PNG, or JPEG — Claude reads it and extracts the transactions</div>
        {fileName && <div className="cc-file-name"><FiFileText /> {fileName}</div>}
      </label>

      {busy && <div className="cc-status"><FiLoader className="cc-spin" /> {progressMsg || 'Working…'}</div>}
      {error && <div className="cc-error"><FiAlertTriangle /> {error}</div>}

      {devMock && (
        <div className="cc-devmock">
          <strong>Dev mock</strong> — no <code>ANTHROPIC_API_KEY</code> on the server, so these are sample rows.
          Add a key to <code>server/.env</code> and restart the API for real extraction.
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="cc-meta">
            <div className="cc-meta-field">
              <label>Statement</label>
              <input value={accountLabel} onChange={e => setAccountLabel(e.target.value)} placeholder="Account label" />
            </div>
            <div className="cc-meta-field cc-meta-narrow">
              <label>Currency</label>
              <input value={currency} onChange={e => setCurrency(e.target.value)} />
            </div>
            <div className="cc-meta-field">
              <label>Belongs to (optional)</label>
              <select value={ownerKey} onChange={e => setOwnerKey(e.target.value)}>
                <option value="">— Unassigned —</option>
                {ownerOptions.map(o => (
                  <option key={o.key} value={o.key}>{o.name} ({o.label})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="cc-grid-head">
            <h3>Review extracted transactions ({keptRows.length} of {rows.length})</h3>
            <p className="cc-grid-note">Edit any cell, untick rows to skip. Nothing is saved until you import.</p>
          </div>

          <div className="cc-table-scroll">
            <table className="cc-table">
              <thead>
                <tr>
                  <th className="cc-c-keep">Keep</th>
                  <th className="cc-c-date">Date</th>
                  <th className="cc-c-desc">Description</th>
                  <th className="cc-c-amt">Amount</th>
                  <th className="cc-c-dir">Dir</th>
                  <th className="cc-c-bal">Balance</th>
                  <th className="cc-c-del" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={r.keep ? '' : 'cc-row-off'}>
                    <td className="cc-c-keep">
                      <input type="checkbox" checked={r.keep} onChange={e => updateRow(i, { keep: e.target.checked })} />
                    </td>
                    <td><input value={r.date} onChange={e => updateRow(i, { date: e.target.value })} /></td>
                    <td><input value={r.description} onChange={e => updateRow(i, { description: e.target.value })} /></td>
                    <td><input className="cc-num" type="number" step="0.01" value={r.amount}
                      onChange={e => updateRow(i, { amount: parseFloat(e.target.value) })} /></td>
                    <td>
                      <select value={r.direction} onChange={e => updateRow(i, { direction: e.target.value as any })}>
                        <option value="debit">debit</option>
                        <option value="credit">credit</option>
                      </select>
                    </td>
                    <td><input className="cc-num" type="number" step="0.01" value={r.balance ?? ''}
                      onChange={e => updateRow(i, { balance: e.target.value === '' ? null : parseFloat(e.target.value) })} /></td>
                    <td className="cc-c-del">
                      <button className="cc-del" title="Remove row" onClick={() => setRows(rs => rs.filter((_, idx) => idx !== i))}>
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cc-actions">
            <button className="cc-btn cc-btn-ghost" onClick={downloadCsv} disabled={keptRows.length === 0}>
              <FiDownload /> Download CSV
            </button>
            <button className="cc-btn cc-btn-primary" onClick={handleImport} disabled={busy || keptRows.length === 0}>
              Import {keptRows.length} transaction{keptRows.length === 1 ? '' : 's'}
            </button>
          </div>

          {importedCount > 0 && (
            <div className="cc-done">
              ✓ Imported <strong>{importedCount}</strong> transaction{importedCount === 1 ? '' : 's'} into Bank Transactions.
              <button className="cc-link" onClick={goToProcess}>Open Process Docs / Receipts <FiArrowRight /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ConvertToCsvTab;
